import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { guestService, guideService } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useSelectedProperty } from "@/stores/property";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, BookOpen, CheckCircle2, Users } from "lucide-react";
import { Link } from "react-router-dom";

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const propertyId = useSelectedProperty();
  const guests = useQuery({
    queryKey: ["guests", propertyId],
    queryFn: () => guestService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });
  const guides = useQuery({
    queryKey: ["guides", propertyId],
    queryFn: () => guideService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });

  // 게시된 안내 / 전체 안내. 숙소를 등록하면 표준 항목 9개가 미게시 초안으로 깔리므로
  // 신규 숙소는 0/9 에서 시작해 9/9 로 채워진다 (분모는 운영자가 항목을 추가하면 함께 는다).
  const allGuides = guides.data ?? [];
  const publishedGuides = allGuides.filter((g) => g.is_published).length;
  const guideProgressTone =
    allGuides.length === 0
      ? "bg-muted text-muted-foreground"
      : publishedGuides === allGuides.length
        ? "bg-green-100 text-green-700"
        : "bg-amber-100 text-amber-700";
  const guideProgressLabel =
    allGuides.length === 0
      ? "안내 없음"
      : publishedGuides === allGuides.length
        ? "모두 게시함"
        : `초안 ${allGuides.length - publishedGuides}개 남음`;

  const stats = [
    {
      label: "등록 게스트",
      badge: undefined as { tone: string; label: string } | undefined,
      value: guests.data?.length ?? 0,
      loading: guests.isLoading,
      icon: Users,
      to: "/admin/reservations",
      tint: "text-primary bg-primary/10",
    },
    {
      label: "가이드 · FAQ",
      badge: undefined as { tone: string; label: string } | undefined,
      value: guides.data?.length ?? 0,
      loading: guides.isLoading,
      icon: BookOpen,
      to: "/admin/guides",
      tint: "text-accent bg-accent/10",
    },
    {
      label: "게시된 안내",
      value: `${publishedGuides}/${allGuides.length}`,
      badge: { tone: guideProgressTone, label: guideProgressLabel },
      loading: guides.isLoading,
      icon: CheckCircle2,
      to: "/admin/guides",
      tint: "text-brass bg-brass/10",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome hero. */}
      <section className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-card">
        <img
          src="/brand/welcome-scene.png"
          alt=""
          className="pointer-events-none absolute inset-y-0 right-0 hidden h-full w-1/2 object-cover object-[50%_45%] opacity-90 sm:block"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-card via-card/95 to-card/30 sm:to-transparent" />
        <div className="relative max-w-md p-6 sm:p-7">
          <p className="eyebrow-brass">컨시어지 콘솔</p>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-foreground">
            안녕하세요{user?.name ? `, ${user.name}님` : ""}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            컨시어지 운영 현황을 한눈에 확인하세요.
          </p>
        </div>
      </section>

      {!propertyId ? (
        <Card>
          <NoPropertySelected />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          {stats.map(({ label, value, badge, loading, icon: Icon, to, tint }) => (
            <Link key={label} to={to} className="group">
              <Card className="h-full transition-all duration-200 group-hover:-translate-y-0.5 group-hover:shadow-elevated">
                <CardHeader className="flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {label}
                  </CardTitle>
                  <span
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-lg transition-transform group-hover:scale-105",
                      tint
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                </CardHeader>
                <CardContent className="flex items-end justify-between">
                  {loading ? (
                    <span className="skeleton h-9 w-14" />
                  ) : (
                    <div className="space-y-1">
                      <p className="font-display text-4xl font-medium tabular-nums tracking-tight">
                        {value}
                      </p>
                      {badge && (
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                            badge.tone
                          )}
                        >
                          {badge.label}
                        </span>
                      )}
                    </div>
                  )}
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
